import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  DEFAULT_CALIBRATIONS,
  SENSORS_BY_TYPE,
  calibrateBattery,
  calibrateMoisture,
  calibrateWaterLevel,
  type Calibration,
  type CalibrationParams,
  type DeviceDetail,
  type LinearMapParams,
  type LipoDividerParams,
  type LipoMvParams,
  type PressureXgzpParams,
  type Sensor,
} from '@irrigazione/shared';
import { api } from '../api/client';
import { Field, Notice } from './ui';

const SENSOR_LABELS: Record<Sensor, string> = {
  battery: 'Batteria',
  moisture: 'Umidità del terreno',
  water_level: 'Livello del serbatoio',
};

/**
 * I controller inviano solo raw: qui si decide come diventano grandezze fisiche.
 * L'anteprima usa le stesse funzioni del server (da `@irrigazione/shared`),
 * quindi il valore mostrato mentre si regola e' esattamente quello che verra'
 * salvato in tabella.
 */
export function CalibrationTab({ device }: { device: DeviceDetail }) {
  const queryClient = useQueryClient();
  const sensors = SENSORS_BY_TYPE[device.type];

  const recompute = useMutation({
    mutationFn: () => api.recomputeHistory(device.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device', device.id] });
      queryClient.invalidateQueries({ queryKey: ['history', device.id] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  return (
    <div className="stack">
      <Notice>
        I valori raw restano immutati nello storico: la calibrazione è solo il modo in cui vengono
        interpretati. Dopo una modifica usa «Ricalcola storico» per riallineare anche i dati passati.
      </Notice>

      {sensors.map((sensor) => (
        <SensorCalibration
          key={sensor}
          device={device}
          sensor={sensor}
          calibration={device.calibrations.find((c) => c.sensor === sensor)}
        />
      ))}

      <section className="card row row-wrap" style={{ justifyContent: 'space-between' }}>
        <div>
          <strong className="small">Ricalcola lo storico</strong>
          <p className="tiny muted" style={{ margin: 0 }}>
            Riapplica le calibrazioni correnti a tutte le letture già registrate.
          </p>
        </div>
        <button className="btn" onClick={() => recompute.mutate()} disabled={recompute.isPending}>
          {recompute.isPending ? 'Ricalcolo…' : 'Ricalcola storico'}
        </button>
      </section>
      {recompute.data && (
        <p className="tiny muted" style={{ margin: 0 }}>
          {recompute.data.updated} letture aggiornate.
        </p>
      )}
    </div>
  );
}

function SensorCalibration({
  device,
  sensor,
  calibration,
}: {
  device: DeviceDetail;
  sensor: Sensor;
  calibration?: Calibration;
}) {
  const queryClient = useQueryClient();
  const fallback = DEFAULT_CALIBRATIONS[device.type][sensor];
  const kind = calibration?.kind ?? fallback?.kind;
  const [params, setParams] = useState<CalibrationParams>(
    (calibration?.params ?? fallback?.params ?? {}) as CalibrationParams,
  );
  const [dirty, setDirty] = useState(false);

  const save = useMutation({
    mutationFn: () => api.saveCalibration(device.id, sensor, kind!, params),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['device', device.id] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  if (!kind) return null;

  const reading = device.lastReading;
  const currentRaw =
    sensor === 'battery'
      ? reading?.batteryRaw
      : sensor === 'moisture'
        ? reading?.moistureRaw
        : reading?.waterLevelRaw;

  const patch = (values: Record<string, number>) => {
    setParams((current) => ({ ...current, ...values }) as CalibrationParams);
    setDirty(true);
  };

  return (
    <section className="card stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{SENSOR_LABELS[sensor]}</h3>
        <span className="tiny muted">raw attuale: {currentRaw ?? '—'}</span>
      </div>

      {kind === 'lipo_divider' && (
        <DividerFields params={params as LipoDividerParams} patch={patch} />
      )}
      {kind === 'linear_map' && (
        <LinearFields
          params={params as LinearMapParams}
          patch={patch}
          currentRaw={currentRaw ?? null}
        />
      )}
      {kind === 'pressure_xgzp' && (
        <PressureFields
          params={params as PressureXgzpParams}
          patch={patch}
          currentRaw={currentRaw ?? null}
        />
      )}
      {kind === 'lipo_mv' && <CurveInfo params={params as LipoMvParams} />}

      <Preview kind={kind} params={params} raw={currentRaw ?? null} />

      <div className="row">
        <button
          className="btn btn--primary btn--sm"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
        >
          Salva calibrazione
        </button>
        {dirty && <span className="tiny muted">Modifiche non salvate</span>}
      </div>
      {save.error && <Notice variant="error">{(save.error as Error).message}</Notice>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Form per tipo di calibrazione
// ---------------------------------------------------------------------------

type Patch = (values: Record<string, number>) => void;

function DividerFields({ params, patch }: { params: LipoDividerParams; patch: Patch }) {
  return (
    <>
      <p className="tiny muted" style={{ margin: 0 }}>
        Il partitore riporta la tensione della cella dentro il fondo scala dell&apos;ADC. Usa gain e
        offset per compensare la tolleranza reale delle resistenze rispetto a un multimetro.
      </p>
      <div className="fields">
        <Field label="R1 verso V+ (kΩ)">
          <input type="number" step="0.1" value={params.r1_kohm}
            onChange={(e) => patch({ r1_kohm: Number(e.target.value) })} />
        </Field>
        <Field label="R2 verso massa (kΩ)">
          <input type="number" step="0.1" value={params.r2_kohm}
            onChange={(e) => patch({ r2_kohm: Number(e.target.value) })} />
        </Field>
        <Field label="Riferimento ADC (V)">
          <input type="number" step="0.01" value={params.vref}
            onChange={(e) => patch({ vref: Number(e.target.value) })} />
        </Field>
        <Field label="Fondo scala ADC">
          <input type="number" value={params.adc_max}
            onChange={(e) => patch({ adc_max: Number(e.target.value) })} />
        </Field>
        <Field label="Gain">
          <input type="number" step="0.001" value={params.gain}
            onChange={(e) => patch({ gain: Number(e.target.value) })} />
        </Field>
        <Field label="Offset (mV)">
          <input type="number" step="1" value={params.offset_mv}
            onChange={(e) => patch({ offset_mv: Number(e.target.value) })} />
        </Field>
      </div>
    </>
  );
}

function LinearFields({
  params,
  patch,
  currentRaw,
}: {
  params: LinearMapParams;
  patch: Patch;
  currentRaw: number | null;
}) {
  return (
    <>
      <p className="tiny muted" style={{ margin: 0 }}>
        Sonda capacitiva: il valore raw <em>scende</em> quando il terreno è bagnato. Misura la sonda
        all&apos;aria e poi immersa in acqua, e cattura i due riferimenti.
      </p>
      <div className="fields">
        <Field label="Raw a secco (sonda in aria)">
          <div className="row">
            <input type="number" value={params.raw_dry}
              onChange={(e) => patch({ raw_dry: Number(e.target.value) })} />
            <CaptureButton raw={currentRaw} onCapture={(raw) => patch({ raw_dry: raw })} />
          </div>
        </Field>
        <Field label="Raw in acqua">
          <div className="row">
            <input type="number" value={params.raw_wet}
              onChange={(e) => patch({ raw_wet: Number(e.target.value) })} />
            <CaptureButton raw={currentRaw} onCapture={(raw) => patch({ raw_wet: raw })} />
          </div>
        </Field>
      </div>
    </>
  );
}

function PressureFields({
  params,
  patch,
  currentRaw,
}: {
  params: PressureXgzpParams;
  patch: Patch;
  currentRaw: number | null;
}) {
  return (
    <>
      <p className="tiny muted" style={{ margin: 0 }}>
        Il sensore invia i 24 bit grezzi del registro di pressione. Il fattore K dipende dal fondo
        scala del modulo (vedi datasheet) e resta modificabile qui, non nel firmware.
      </p>
      <div className="fields">
        <Field label="Fattore K">
          <input type="number" value={params.k_factor}
            onChange={(e) => patch({ k_factor: Number(e.target.value) })} />
        </Field>
        <Field label="Raw a serbatoio vuoto">
          <div className="row">
            <input type="number" value={params.raw_offset}
              onChange={(e) => patch({ raw_offset: Number(e.target.value) })} />
            <CaptureButton raw={currentRaw} onCapture={(raw) => patch({ raw_offset: raw })} />
          </div>
        </Field>
        <Field label="Altezza utile (cm)">
          <input type="number" step="0.5" value={params.tank_height_cm}
            onChange={(e) => patch({ tank_height_cm: Number(e.target.value) })} />
        </Field>
        <Field label="Densità relativa">
          <input type="number" step="0.01" value={params.density}
            onChange={(e) => patch({ density: Number(e.target.value) })} />
        </Field>
      </div>
    </>
  );
}

function CurveInfo({ params }: { params: LipoMvParams }) {
  return (
    <>
      <p className="tiny muted" style={{ margin: 0 }}>
        Il nodo CubeCell riporta già i millivolt: la percentuale viene dalla curva di scarica LiPo.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Tensione</th>
              <th>Carica</th>
            </tr>
          </thead>
          <tbody>
            {(params.curve ?? []).map((point) => (
              <tr key={point.mv}>
                <td className="num">{(point.mv / 1000).toFixed(2)} V</td>
                <td className="num">{point.pct} %</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CaptureButton({
  raw,
  onCapture,
}: {
  raw: number | null;
  onCapture: (raw: number) => void;
}) {
  return (
    <button
      type="button"
      className="btn btn--sm"
      disabled={raw == null}
      onClick={() => raw != null && onCapture(raw)}
      title={raw == null ? 'Nessuna lettura disponibile' : `Usa il raw attuale (${raw})`}
    >
      Cattura
    </button>
  );
}

/** Anteprima dal vivo: applica i parametri correnti all'ultima lettura reale. */
function Preview({
  kind,
  params,
  raw,
}: {
  kind: Calibration['kind'];
  params: CalibrationParams;
  raw: number | null;
}) {
  if (raw == null) {
    return (
      <p className="tiny muted" style={{ margin: 0 }}>
        Nessuna lettura disponibile per l&apos;anteprima.
      </p>
    );
  }

  let text: string;
  try {
    if (kind === 'linear_map') {
      text = `${calibrateMoisture(params, raw).toFixed(1)} %`;
    } else if (kind === 'pressure_xgzp') {
      const level = calibrateWaterLevel(params, raw);
      text = `${level.pct.toFixed(1)} % · ${level.cm.toFixed(1)} cm · ${level.kpa.toFixed(2)} kPa`;
    } else {
      const battery = calibrateBattery(kind, params, raw);
      text = `${battery.pct.toFixed(1)} % · ${(battery.mv / 1000).toFixed(2)} V`;
    }
  } catch {
    text = 'parametri non validi';
  }

  return (
    <div className="notice">
      <span>
        Con questi parametri il raw <strong>{raw}</strong> vale <strong>{text}</strong>
      </span>
    </div>
  );
}
